import React, { Fragment, useEffect, useRef, useState } from 'react';
import useFieldRevealTracker from '../../hooks/useFieldRevealTracker';
import VgsShowValue from '../VgsInput/VgsShowValue';
import AddressDisplay from '../Address/AddressDisplay';
import EditPhoneModal from './EditPhoneNumber';
import useModal from '../../hooks/useModal';
import submit from './vgsUtils/submit';
import { StripeEnv } from '../../util/enums';
import { createVgsCollectForm } from '../../services/vgs/collect/form';
import useVgsScripts from '../VgsInput/hooks/useVgsScripts';
import { SnapIcon } from '../SnapIcon';

export default function KybDisplay({
  kyb,
  setRefreshKyb,
  onEdit,
  onRevealSuccess,
  onRevealFail,
  token,
  allowPhoneNumberUpdate = true,
}: {
  kyb: Kyb;
  setRefreshKyb: (refresh: symbol) => void;
  onEdit: () => void;
  onRevealSuccess: () => void;
  onRevealFail: () => void;
  token: string;
  allowPhoneNumberUpdate?: boolean;
}): JSX.Element {
  const fieldsToTrack = [
    'customerFacingName',
    'type',
    'phoneNumber',
    'taxId',
    'address',
  ];
  // only wait for kyb.url to be rendered if it has a value
  if (kyb.url) {
    fieldsToTrack.push('url');
  }
  const onFieldRevealSuccess = useFieldRevealTracker(
    onRevealSuccess,
    fieldsToTrack,
  );
  const { isOpen: isEditPhoneModalOpen, toggleModal: toggleEditPhoneModal } =
    useModal();
  const [refreshVgsCollect, setRefreshVgsCollect] = useState(Symbol());
  const updateKybForm = useRef<VGSForm<{ vaultKybUpdate: string }>>();
  const vgsScriptsLoaded = useVgsScripts();

  useEffect((): void => {
    if (!vgsScriptsLoaded) {
      return;
    }
    if (!updateKybForm.current) {
      updateKybForm.current = createVgsCollectForm();
    }
  }, [vgsScriptsLoaded, refreshVgsCollect]);

  return (
    <Fragment>
      {updateKybForm.current && (
        <EditPhoneModal
          isOpen={isEditPhoneModalOpen}
          toggle={toggleEditPhoneModal}
          form={updateKybForm.current}
          onSubmit={async () => {
            const submitter = await submit({
              stripeEnv: StripeEnv.RAISE,
              kybId: kyb.id as string,
              onSuccess: () => {
                setRefreshKyb(Symbol());
                setRefreshVgsCollect(Symbol());
                toggleEditPhoneModal();
              },
            });
            if (updateKybForm.current) {
              await submitter({
                form: updateKybForm.current,
                provider: 'stripe',
              });
              updateKybForm.current = createVgsCollectForm();
            }
          }}
        />
      )}

      <div className='md:flex mb-5'>
        <div className='md:w-1/2 my-2'>
          <VgsShowValue
            value={kyb.customerFacingName}
            label='Payable Name'
            type='text'
            onRevealSuccess={onFieldRevealSuccess('customerFacingName')}
            onRevealFail={onRevealFail}
            token={token}
          />
        </div>

        <div className='md:w-1/2 my-2'>
          <VgsShowValue
            value={kyb.type}
            label='Type'
            type='text'
            onRevealSuccess={onFieldRevealSuccess('type')}
            onRevealFail={onRevealFail}
            token={token}
          />
        </div>
      </div>

      {kyb.url ? (
        <div className='md:flex mb-5'>
          <div className='md:w-1/2 my-2'>
            <VgsShowValue
              value={kyb.url}
              label='Website'
              type='text'
              onRevealSuccess={onFieldRevealSuccess('url')}
              onRevealFail={onRevealFail}
              token={token}
            />
          </div>
        </div>
      ) : null}

      <div className='md:flex mb-5'>
        <div className='md:w-1/2 my-2'>
          <div className='flex items-center'>
            <VgsShowValue
              value={kyb.phoneNumber}
              label='Phone Number'
              type='text'
              onRevealSuccess={onFieldRevealSuccess('phoneNumber')}
              onRevealFail={onRevealFail}
              replacePatterns={[
                ['\\D', ''],
                ['(\\d{3})(\\d{3})(\\d{4})', '($1) $2-$3'],
              ]}
              token={token}
              // refreshVgsShow={refreshVgsShow}
            />
            {allowPhoneNumberUpdate && (
              <SnapIcon
                icon='pencil-alt'
                as='button'
                onClick={toggleEditPhoneModal}
                className='rounded-full border border-blue-900 bg-blue-600 text-white hover:bg-blue-800 active:bg-blue-800 active:border-blue-500 mr-2'
                size='lg'
              />
            )}
          </div>
        </div>

        <div className='md:w-1/2 my-2'>
          <VgsShowValue
            value={kyb.taxId}
            label='Tax ID'
            type='text'
            onRevealSuccess={onFieldRevealSuccess('taxId')}
            onRevealFail={onRevealFail}
            token={token}
          />
        </div>
      </div>

      <AddressDisplay
        address={kyb.address}
        onRevealSuccess={onFieldRevealSuccess('address')}
        onRevealFail={onRevealFail}
        token={token}
      />
      <div className='md:flex mb-5'>
        <div className='md:w-1/2 my-2'>
          <a className='link' onClick={onEdit}>
            Report Incorrect or Missing Information
          </a>
        </div>
      </div>
    </Fragment>
  );
}
